import { useState, useMemo, useEffect, useReducer } from 'react';
import { Box, Stack } from '@mui/material';
import { ApplicationAccessType, CommunicationType } from 'domains/content/types';
import ContentNameInput from 'domains/core/components/ContentNameInput';
import ErrorSnackbar from 'domains/core/components/Snackbars/ErrorSnackbar';
import useQueryParams from 'hooks/useQueryParams';
import { useHistory } from 'react-router-dom';
import { URLPaths } from 'models/enums';
import useWhatsApp from 'hooks/queries/useWhatsApp';
import useCreateWhatsApp from 'hooks/mutations/useCreateWhatsApp';
import useUpdateWhatsApp from 'hooks/mutations/useUpdateWhatsApp';
import useContactColumns from 'hooks/queries/useContactColumns';
import ContentStatusText, { ContentStatus } from 'pages/ContentPage/ContentTable/ContentStatusText';
import { useFlags } from 'launchdarkly-react-client-sdk';
import WhatsAppContentWrapper from './components/WhatsAppContentWrapper';
import WhatsAppHeaderSection from './components/WhatsAppHeaderSection';
import { whatsAppReducer, whatsAppInitialState, initializeWhatsAppState } from './whatsAppReducer';
import { setName, setBodyText, setApplicationsAccess, setCommunicationType, setWhatsAppState } from './whatsAppActions';
import { getBodyComponentText } from './utils/whatsAppUtils';
import useFormatWhatsAppContent from './hooks/useFormatWhatsAppContent';
import { getMergeTags } from 'utils';

const ContentWhatsAppBuilderPage = () => {
    const [state, dispatch] = useReducer(whatsAppReducer, whatsAppInitialState);
    const [isNameTouched, setIsNameTouched] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const { contentDraft: hasContentDraft } = useFlags();
    const history = useHistory();

    // URL params
    const query = useQueryParams();
    const whatsappTemplateId = query.get('id');
    const parentFolderId = Number(query.get('parentFolderId'));
    const isCopy = query.get('isCopy') === 'true';
    // Status from URL is prioritized over API response to handle race condition when converting
    // published content to draft - the API cache might return stale "published" status before refetch completes
    const statusFromUrl = query.get('status') as ContentStatus | null;

    // Fetch WhatsApp template
    const { data: whatsAppData, isInitialLoading: isWhatsAppLoading, isSuccess: isWhatsAppSuccess } = useWhatsApp(
        whatsappTemplateId,
        {
            enabled: !!whatsappTemplateId && whatsappTemplateId !== 'default',
        }
    );

    // Fetch contact columns for merge tags preview (similar to SMS)
    const { data: contactColumns, isSuccess: isContactColumnsSuccess } = useContactColumns();

    // Initialize state from API data
    useEffect(() => {
        if (!whatsappTemplateId) {
            // New content - set defaults (WhatsApp templates default to InJourney and Commercial)
            dispatch(setName('Untitled'));
            dispatch(setApplicationsAccess([ApplicationAccessType.INJOURNEY]));
            dispatch(setCommunicationType(CommunicationType.COMMERCIAL));
        }
    }, [whatsappTemplateId]);

    useEffect(() => {
        if (isWhatsAppSuccess && whatsAppData) {
            const initializedState = initializeWhatsAppState(whatsAppData, isCopy);
            // Prioritize URL status (set when converting published to draft) over API response
            if (statusFromUrl) {
                initializedState.status = statusFromUrl;
            }
            dispatch(setWhatsAppState(initializedState));
        }
    }, [isWhatsAppSuccess, whatsAppData, isCopy, statusFromUrl]);

    // Extract content from BODY component for editor
    const content = useMemo(() => getBodyComponentText({ components: state.components }), [state.components]);

    // Create merge tags from contact columns for preview (similar to SMS)
    const mergeTags = useMemo(() => {
        if (isContactColumnsSuccess && contactColumns) {
            return getMergeTags(contactColumns);
        }
        return [];
    }, [contactColumns, isContactColumnsSuccess]);

    // Format content for preview - replace {{first_name}} with preview values like "Taylor"
    const formattedPreviewContent = useFormatWhatsAppContent(content, mergeTags);

    // Navigate to content list
    const navigateToContent = () => {
        const contentPath = parentFolderId ? `${URLPaths.CONTENT_FOLDER}/${parentFolderId}` : URLPaths.CONTENT;
        history.push(contentPath);
    };

    // Create mutation
    const { mutate: createWhatsApp, isLoading: isCreateLoading, error: createError } = useCreateWhatsApp({
        onSuccess: navigateToContent,
    });

    // Update mutation
    const { mutate: updateWhatsApp, isLoading: isUpdateLoading, error: updateError } = useUpdateWhatsApp(
        whatsappTemplateId,
        {
            onSuccess: navigateToContent,
        }
    );

    // Determine if this is a create or update operation
    const isValidId = whatsappTemplateId && whatsappTemplateId !== 'default' && !isCopy;
    const isUpdate = !!isValidId;

    const handleChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!isNameTouched) {
            setIsNameTouched(true);
        }
        dispatch(setName(e.target.value));
    };

    const handleSave = (status?: ContentStatus) => {
        const mutateInput = {
            name: state.name,
            language: state.language,
            components: state.components,
            communicationType: state.communicationType,
            applicationsAccess: state.applicationsAccess.join(','),
            ...(state.whatsappAccountId && { whatsappAccountId: state.whatsappAccountId }),
            ...(state.whatsappNamespace && { whatsappNamespace: state.whatsappNamespace }),
            ...(state.whatsappTemplateId && { whatsappTemplateId: state.whatsappTemplateId }),
            ...(status && { status }),
            ...(parentFolderId && !isUpdate && { parentFolderId }),
        };

        isValidId ? updateWhatsApp({ whatsappTemplateId, ...mutateInput }) : createWhatsApp(mutateInput);
    };

    // Update error message from API errors
    useEffect(() => {
        if (createError) {
            setErrorMessage(createError.message);
        } else if (updateError) {
            setErrorMessage(updateError.message);
        }
    }, [createError, updateError]);

    // Validation
    const isNameUndefinedOnTouch = !state.name && isNameTouched;
    const isNameTooShort = isNameTouched && state.name.length < 3;
    const nameHelperText = isNameUndefinedOnTouch
        ? 'Please enter a WhatsApp name'
        : isNameTooShort
        ? 'WhatsApp name must have at least 3 characters'
        : '';

    // Loading and error states
    const isLoading = isWhatsAppLoading || isCreateLoading || isUpdateLoading;
    const isError = !!errorMessage || !!createError || !!updateError;
    const errorDisplayMessage = errorMessage || createError?.message || updateError?.message;

    return (
        <Stack height="100%" mb={4} rowGap={4}>
            {isError && <ErrorSnackbar errorMessage={errorDisplayMessage} />}
            <Box sx={{ display: 'inline-flex', alignItems: 'flex-start', gap: 2 }}>
                <ContentNameInput
                    dataTestId="name-input"
                    disabled={isLoading}
                    error={isNameUndefinedOnTouch || isNameTooShort}
                    helperText={nameHelperText}
                    name={state.name}
                    onChange={handleChangeName}
                    withClickAway
                />
                {hasContentDraft && (
                    <Box sx={{ pt: 3 }}>
                        <ContentStatusText
                            status={
                                whatsappTemplateId && whatsappTemplateId !== 'default' && !isCopy
                                    ? state.status || ' '
                                    : ContentStatus.DRAFT
                            }
                        />
                    </Box>
                )}
            </Box>

            <WhatsAppHeaderSection isLoading={isLoading} onSaveAndExit={handleSave} content={content} />
            <WhatsAppContentWrapper
                content={content}
                formattedPreviewContent={formattedPreviewContent}
                setContent={(text) => dispatch(setBodyText(text))}
            />
        </Stack>
    );
};

export default ContentWhatsAppBuilderPage;
